import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";
import type { PaginationState } from "./usePagination";

type InvoiceStatus = Database["public"]["Enums"]["invoice_status"];

// Includes a pseudo-filter "needs_review" that surfaces invoices missing
// a customer or project assignment (typically from QB imports).
export type InvoiceFilterStatus = InvoiceStatus | "all" | "needs_review";

export const useInvoices = (
  statusFilter: InvoiceFilterStatus = "all",
  pagination?: PaginationState,
  search?: string,
) => {
  const { user } = useAuth();
  const trimmedSearch = search?.trim() || "";

  return useQuery({
    queryKey: ["invoices", statusFilter, user?.id, pagination?.page, trimmedSearch],
    queryFn: async () => {
      if (pagination) {
        let countQuery = supabase
          .from("invoices")
          .select("id", { count: "exact", head: true })
          .is("deleted_at", null);
        if (statusFilter === "needs_review") {
          countQuery = countQuery.or("needs_customer_assignment.eq.true,needs_project_assignment.eq.true");
        } else if (statusFilter !== "all") {
          countQuery = countQuery.eq("status", statusFilter);
        }
        if (trimmedSearch) {
          countQuery = countQuery.ilike("invoice_number", `%${trimmedSearch}%`);
        }
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query = supabase
        .from("invoices")
        .select(
          "id, invoice_number, invoice_date, total, status, approved_by, approved_at, sent_at, paid_at, payment_reference, payment_method, due_date, amount_paid, notes, created_at, project_id, projects(name), customer_id, customers(name), source, needs_customer_assignment, needs_project_assignment"
        )
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (statusFilter === "needs_review") {
        query = query.or("needs_customer_assignment.eq.true,needs_project_assignment.eq.true");
      } else if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      if (trimmedSearch) {
        query = query.ilike("invoice_number", `%${trimmedSearch}%`);
      }

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user,
  });
};
